import React from 'react';
import { useTranslation } from 'react-i18next';
import InfoIcon from '@mui/icons-material/Info';
import PreviewTooltip from '../library-category-card/components/preview-block/PreviewTooltip';
import styles from './CategoryCard.module.scss';
import { CategoryCardProps } from './types';

const CategoryCard: React.FC<CategoryCardProps> = ({
  id,
  title,
  subtitle,
  countLabel,
  description,
  icon,
  accent,
  onClick,
  hasNewLabel,
  hasFreeLabel,
  previewBlock,
  actions,
}) => {
  const { t } = useTranslation();

  const cardClassName = [
    styles.card,
    styles[`accent_${accent}`],
    description ? styles.with_description : '',
    previewBlock ? styles.with_preview : '',
    actions ? styles.with_actions : '',
    onClick ? styles.clickable : '',
  ]
    .filter(Boolean)
    .join(' ');

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onClick || e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      id={id}
      className={cardClassName}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={onClick ? [title, subtitle, countLabel].filter(Boolean).join(', ') : undefined}
    >
      <div className={styles.layout}>
        <div id={`${id}IconContainer`} className={styles.icon_container} aria-hidden='true'>
          {icon}
        </div>
        <div id={`${id}Content`} className={styles.content}>
          <div className={styles.title_row}>
            <h3 id={`${id}Title`} className={styles.title} title={actions ? title : undefined}>
              {title}
            </h3>
            {hasNewLabel && (
              <span id={`${id}NewLabel`} className={styles.new_label}>
                {t('labels.new')}
              </span>
            )}
            {hasFreeLabel && (
              <span id={`${id}FreeLabel`} className={styles.free_label}>
                {t('labels.free')}
              </span>
            )}
          </div>
          {subtitle && (
            <p
              id={`${id}Subtitle`}
              className={styles.subtitle}
              title={actions ? subtitle : undefined}
            >
              {subtitle}
            </p>
          )}
          {countLabel && (
            <p id={`${id}Count`} className={styles.count}>
              {countLabel}
            </p>
          )}
          {description && (
            <p id={`${id}Description`} className={styles.description}>
              {description}
            </p>
          )}
        </div>
      </div>
      {actions && (
        <div id={`${id}Actions`} className={styles.actions}>
          {actions}
        </div>
      )}
      {previewBlock && (
        <PreviewTooltip title={previewBlock} arrow placement='bottom'>
          <InfoIcon
            id={`${id}InfoIcon`}
            className={styles.preview_icon}
            color='primary'
            fontSize='small'
            onClick={(e) => e.stopPropagation()}
          />
        </PreviewTooltip>
      )}
    </div>
  );
};

export default CategoryCard;
